import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

import { Alert } from "../../components/base/Alert";
import { Select } from "../../components/base/Select";
import SettingsTabs from "../../components/Settings/SettingsTabs";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { FailureList, RunHistoryStrip, RunHistoryTable } from "../../components/SyncTracker/RunHistory";
import { SyncOverview } from "../../components/SyncTracker/SyncOverview";
import { formatAgo, useNow } from "../../components/SyncTracker/syncMeta";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useImportSyncCustomers, useImportSyncStatus } from "../../data/ImportSync/useImportSync";

const LAST_CUSTOMER_KEY = "sync-tracker:customer";

function readLastCustomer() {
  try {
    return localStorage.getItem(LAST_CUSTOMER_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function rememberCustomer(id: string) {
  try {
    localStorage.setItem(LAST_CUSTOMER_KEY, id);
  } catch {
    // Storage blocked — the URL still carries the choice.
  }
}

/**
 * How each customer's nightly Pinnacle sync is getting on: the run in progress, the last one that
 * completed, and recent history. The runs are recorded in the customer's own Core by the console
 * on their machine; Barney reads them through.
 */
export default function SyncTrackerPage() {
  const { setInfo } = usePageNameContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const customers = useImportSyncCustomers();

  useEffect(() => {
    setInfo({
      name: "Settings",
      desc: "Nightly Pinnacle import progress for each customer",
      pageTitle: "Sync tracker",
    });
  }, []);

  // The URL wins so a link to a customer's tracker opens on them; otherwise the last one viewed,
  // otherwise the only one there is.
  const customerId = useMemo(() => {
    const list = customers.data ?? [];
    const requested = searchParams.get("customer") ?? readLastCustomer();

    if (requested && list.some((c) => c.id === requested)) return requested;
    return list.length === 1 ? list[0].id : undefined;
  }, [customers.data, searchParams]);

  const selectCustomer = (id: string) => {
    rememberCustomer(id);
    setSearchParams(id ? { customer: id } : {});
  };

  const status = useImportSyncStatus(customerId);
  const now = useNow(5_000);

  return (
    <div className="mt-4 space-y-6">
      <SettingsTabs />

      <div className="flex flex-wrap items-end justify-between gap-4">
        <Select
          label="Customer"
          value={customerId ?? ""}
          onChange={(e) => selectCustomer((e.target as HTMLSelectElement).value)}
          disabled={customers.isLoading}
          containerClassName="w-full sm:w-80"
          fullWidth
        >
          <option value="">{customers.isLoading ? "Loading customers…" : "Choose a customer"}</option>
          {customers.data?.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </Select>

        {customerId && (
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {status.data?.current && (
              <span className="inline-flex items-center gap-1.5 font-medium text-blue-700">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
                </span>
                Live
              </span>
            )}
            <span>
              Updated {status.dataUpdatedAt ? formatAgo(new Date(status.dataUpdatedAt).toISOString(), now) : "—"}
            </span>
            <button
              type="button"
              onClick={() => status.refetch()}
              className="inline-flex items-center gap-1 rounded px-2 py-1 font-medium text-gray-600 hover:bg-gray-100"
            >
              <ArrowPathIcon className={clsx("h-4 w-4", status.isFetching && "animate-spin")} aria-hidden />
              Refresh
            </button>
          </div>
        )}
      </div>

      {customers.isError && <Alert severity="error" message={customers.error.message} />}

      {customers.data?.length === 0 && (
        <Alert
          severity="info"
          message="No customers have WilmaPro:ApiUrl and WilmaPro:ApiToken set, so there is no Core to read sync runs from."
        />
      )}

      {!customerId && (customers.data?.length ?? 0) > 1 && (
        <p className="text-sm text-gray-500">Choose a customer to see their sync.</p>
      )}

      {customerId && status.isLoading && (
        <div className="min-h-[240px]">
          <LoadingWheel />
        </div>
      )}

      {customerId && status.isError && (
        <Alert severity="warn" title="Could not load this customer's sync" message={status.error.message} />
      )}

      {customerId && status.data && (
        <>
          <SyncOverview status={status.data} />

          <FailuresSection status={status.data} />

          {status.data.recent.length > 0 && (
            <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-5">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Recent runs</h2>
                <p className="text-sm text-gray-500">
                  Last {status.data.recent.length}, oldest on the left. Click a row with failures to see them.
                </p>
              </div>
              <RunHistoryStrip runs={status.data.recent} />
              <RunHistoryTable customerId={customerId} runs={status.data.recent} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

function FailuresSection({ status }: { status: NonNullable<ReturnType<typeof useImportSyncStatus>["data"]> }) {
  const run = status.current ?? status.lastCompleted;

  if (!run || run.failedVehicles === 0) return null;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-3">
      <h2 className="text-lg font-medium text-gray-900">
        {status.current ? "Failures so far" : "Failures in the last completed sync"}
      </h2>
      <FailureList failures={run.failures} total={run.failedVehicles} />
    </section>
  );
}
